import {
    ColDef,
    ColGroupDef,
    ICellRendererParams,
    ValueGetterParams,
} from 'ag-grid-community';
import {
    LAST_YEAR_COLUMN_STYLE,
    GREY_HEADER_CLASS,
} from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/gridHelpers';
import { DEFAULT_AMOUNT_COLUMN } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/defaultColDefs';
import { getAgentColumn } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/agentColumnHelpers';
import { PayComponent } from 'routes/HumanResourcesModule/PayState/helpers/constants';
import {
    DashDateString,
    PayComponentSalaryMassLine,
} from 'routes/HumanResourcesModule/business/types';
import { getGroupedOrgChartHrColumn } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/orgChartHrColumnHelpers';
import { getPayComponentSalaryField, sortPayComponents } from './dataHelpers';
import { isFooterRow } from 'routes/HumanResourcesModule/Simulation/helpers/gridHelpers';
import { getMantyStatusColumn } from 'routes/HumanResourcesModule/PayState/helpers/colDefinitions';

/**
 * The 'other' pay component is a custom pay component showing the difference between the BRUT pay component amount
 * and the sum amount of all the other PayComponents excluding the Charges patronales.
 */
const otherPayComponentValueGetter =
    (yearSuffix: DashDateString) =>
    (
        params: ValueGetterParams<PayComponentSalaryMassLine>,
    ): number | undefined => {
        if (params === undefined || params.data === undefined) {
            return undefined;
        }

        // If we don't have a `orgChartHrName` we are in the bottomPinnedRow
        // and we are trying to return the total value.
        // The params object is already the aggregation of all the row values,
        // so we just want to return the value of the column field.
        if (
            params.data?.orgChartHrName === undefined &&
            params.colDef.field !== undefined
        ) {
            // @ts-expect-error The field name is defined at runtime and cannot be typed.
            return params.data[params.colDef.field];
        }

        const brutField = getPayComponentSalaryField(
            PayComponent.BRUT,
            yearSuffix,
        );
        const brutAmount = params.data[brutField];
        const employerCostsField = getPayComponentSalaryField(
            PayComponent.EMPLOYER_COSTS,
            yearSuffix,
        );
        // get all the fields corresponding to payComponents different than brut and
        // charges patronales
        const excludedFields: Array<keyof PayComponentSalaryMassLine> = [
            brutField,
            employerCostsField,
        ];
        const nonChargedPayComponentFields = Object.keys(params.data).filter(
            (field) =>
                field.endsWith(yearSuffix) && !excludedFields.includes(field),
        );
        // compute the total for these pay components
        const nonChargedPayComponentTotal = nonChargedPayComponentFields.reduce(
            (total, field) => total + Number(params.data?.[field]),
            0,
        );
        return Number(brutAmount - nonChargedPayComponentTotal);
    };

const getPayComponentsColumns = (
    payComponents: PayComponent[],
    exercice: number,
    month: number,
    showLastYearColumns: boolean,
    showOtherPayComponentColumn: boolean,
): ColDef[] => {
    const displayedPayComponents = showOtherPayComponentColumn
        ? [...payComponents, PayComponent.OTHER]
        : payComponents;
    const displayedMonth = month === 0 ? 1 : month;
    return displayedPayComponents
        .sort(sortPayComponents)
        .flatMap((payComponent) => {
            const paddedMonth = String(displayedMonth).padStart(2, '0');
            const previousYearSuffix: DashDateString =
                `${exercice - 1}-${paddedMonth}-01` as DashDateString;
            const currentYearSuffix: DashDateString =
                `${exercice}-${paddedMonth}-01` as DashDateString;

            const valueGetter = (yearSuffix: DashDateString) =>
                payComponent === PayComponent.OTHER
                    ? otherPayComponentValueGetter(yearSuffix)
                    : undefined;
            const isBrutPayComponent = payComponent === PayComponent.BRUT;
            return [
                {
                    headerName: `${payComponent} ${exercice - 1}`,
                    headerTooltip: payComponent,
                    ...DEFAULT_AMOUNT_COLUMN,
                    //  Brut is only used to calculate the other column
                    hide: !showLastYearColumns || isBrutPayComponent,
                    suppressColumnsToolPanel: isBrutPayComponent,
                    field: getPayComponentSalaryField(
                        payComponent,
                        previousYearSuffix,
                    ),
                    cellStyle: LAST_YEAR_COLUMN_STYLE,
                    headerClass: GREY_HEADER_CLASS,
                    valueGetter: valueGetter(previousYearSuffix),
                    cellRendererSelector: (params: ICellRendererParams) =>
                        params.node.rowPinned
                            ? { component: 'bottomPinnedRowRenderer' }
                            : { component: undefined },
                },
                {
                    ...DEFAULT_AMOUNT_COLUMN,
                    field: getPayComponentSalaryField(
                        payComponent,
                        currentYearSuffix,
                    ),
                    headerName: payComponent,
                    //  Brut is only used to calculate the other column
                    hide: isBrutPayComponent,
                    suppressColumnsToolPanel: isBrutPayComponent,
                    valueGetter: valueGetter(currentYearSuffix),
                    cellRendererSelector: (params: ICellRendererParams) => {
                        if (params.node.rowPinned) {
                            return { component: 'bottomPinnedRowRenderer' };
                        }
                        if (isFooterRow(params)) {
                            return { component: undefined };
                        }
                        return {
                            component: 'lastYearComparatorNumberCellRenderer',
                        };
                    },
                    cellRendererParams: { exercice },
                },
            ];
        });
};

export const getColDefs = ({
    payComponents,
    exercice,
    month,
    showLastYearColumns,
    showOtherPayComponentColumn,
}: {
    payComponents: PayComponent[];
    exercice: number;
    month: number;
    showLastYearColumns: boolean;
    showOtherPayComponentColumn: boolean;
}): (ColDef | ColGroupDef)[] => [
    getGroupedOrgChartHrColumn(),
    getAgentColumn(),
    getMantyStatusColumn(),
    ...getPayComponentsColumns(
        payComponents,
        exercice,
        month,
        showLastYearColumns,
        showOtherPayComponentColumn,
    ),
];
